import { FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ImageIcon } from 'lucide-react';

import { isBlob, getBase64 } from 'utils/utils';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupDropZone } from 'features/common/components/Punchup/primitives/upload/PunchupDropZone';
import { PunchupImagePreview } from 'features/common/components/Punchup/primitives/display/PunchupImagePreview';
import { PunchupFormModuleWrapper } from 'features/common/components/Punchup/composites/forms/PunchupFormModuleWrapper';

import styles from './PunchupImageUpload.module.css';

// ── Types ──────────────────────────────────────────────────────────────────

export interface ImageEntry {
	url: string;
	name: string;
	base64?: string;
}

export interface PunchupImageUploadProps {
	/**
	 * react-hook-form field path — e.g. "mediaContent.eventImage" or "ticketImageStaged".
	 * The field value is an `ImageEntry[]` (empty array = no image).
	 */
	inputId: string;
	/**
	 * Standalone mode only. Called when Cancel is clicked. Any locally-staged
	 * file is discarded first, then this is called so the parent can close the form.
	 */
	onCancel?: () => void;
	/**
	 * When provided, Delete calls this instead of clearing the field locally —
	 * the parent owns the delete (e.g. confirm dialog + delete API). When omitted,
	 * Delete clears the staged field to `[]` (the change persists on the next Save).
	 */
	onDelete?: () => void;
	/** Optional label rendered above the drop zone. */
	label?: string;
	/**
	 * Embedded mode: the upload is one field in a larger form. Renders inside a
	 * grey `PunchupFormModuleWrapper` with no Cancel/Save row — the parent form
	 * owns submission. Only a Delete button is shown (when an image is present).
	 */
	embedded?: boolean;
	/** Max rendered preview height in px. Defaults to 320. */
	maxHeight?: number;
	/**
	 * Maps a staged/persisted entry to the `src` used for preview. Defaults to
	 * `entry.url`. Use this for storage URLs that need a suffix to render
	 * (e.g. Firebase needs `?alt=media` on non-blob URLs).
	 */
	getPreviewSrc?: (entry: ImageEntry) => string;
	/**
	 * The parent form is saving and already covers this component with its own save
	 * overlay spinner. Suppresses the preview's spinner for the duration, so a save
	 * that swaps the entry url (staged blob → uploaded CDN url) shows one indicator
	 * instead of two.
	 */
	isSaving?: boolean;
}

const defaultPreviewSrc = (entry: ImageEntry): string => entry.url;

// ── Component ──────────────────────────────────────────────────────────────

/**
 * PunchupImageUpload
 *
 * Single-image upload with a **two-state** drop-to-replace UX:
 *   empty     → drop zone invitation (Save disabled in standalone)
 *   has-image → image preview inside the drop zone with a hover "drop to replace"
 *               overlay; the zone stays active so dropping a new file re-stages it
 *
 * The image is always **staged locally** (a blob `ImageEntry` carrying base64) and
 * uploaded by the parent on submit — this component never calls an upload API.
 * Image display (loading / error / loaded) is delegated to `PunchupImagePreview`.
 *
 * Two layouts:
 *   - standalone (default): owns the Cancel / Save / Delete button row
 *   - embedded:             wrapped in PunchupFormModuleWrapper, Delete only;
 *                           the parent form commits
 *
 * See CLAUDE.md for the state diagram and prop usage.
 */
export const PunchupImageUpload: FC<PunchupImageUploadProps> = ({
	inputId,
	onCancel,
	onDelete,
	label,
	embedded = false,
	maxHeight = 320,
	getPreviewSrc = defaultPreviewSrc,
	isSaving = false,
}) => {
	const { t } = useTranslation();
	const { setValue, watch } = useFormContext();
	const images = (watch(inputId) ?? []) as ImageEntry[];
	const current = images[0] ?? null;

	const hasImage = current !== null;
	// A blob URL means a new file is staged locally (vs a persisted remote URL).
	const hasStagedFile = current !== null && isBlob(current.url);

	// ── Handlers ─────────────────────────────────────────────────────────

	const handleFileSelect = useCallback(
		async (file: File) => {
			const base64 = await getBase64(file);
			setValue(inputId, [{ url: URL.createObjectURL(file), base64, name: file.name }], { shouldDirty: true });
		},
		[inputId, setValue]
	);

	const handleDelete = () => {
		if (onDelete) onDelete();
		else setValue(inputId, [], { shouldDirty: true });
	};

	const handleCancel = () => {
		// Discard a staged-but-unsaved file so the form returns to its initial value.
		if (hasStagedFile) setValue(inputId, [], { shouldDirty: true });
		onCancel?.();
	};

	// ── Render pieces ────────────────────────────────────────────────────

	const dropZone = (
		<PunchupDropZone accept="image/jpeg,image/png" onFileSelect={handleFileSelect} className={styles.imageZone}>
			{hasImage ? (
				<div className={styles.previewWrapper}>
					<PunchupImagePreview
						src={getPreviewSrc(current)}
						alt={t('image-upload-preview-alt', 'Image preview')}
						maxHeight={maxHeight}
						showSpinner={!isSaving}
					/>
					<div className={styles.previewOverlay}>
						<span className={styles.previewOverlayText}>
							{t('image-upload-click-to-replace', 'Drop or click to replace')}
						</span>
					</div>
				</div>
			) : (
				<div className={styles.emptyContent}>
					<ImageIcon size={32} className={styles.emptyIcon} />
					<span className={styles.emptyTitle}>{t('image-upload-drop-hint', 'Drag & drop or click to select')}</span>
					<span className={styles.emptyMeta}>{t('image-upload-formats', 'JPG, PNG supported')}</span>
				</div>
			)}
		</PunchupDropZone>
	);

	const deleteButton = hasImage && (
		<PunchupButton type="button" variant="destructive" onClick={handleDelete}>
			{t('button-delete')}
		</PunchupButton>
	);

	const body = (
		<div className={styles.uploadRoot}>
			{label && <span className={styles.fieldLabel}>{label}</span>}
			{dropZone}

			{embedded ? (
				deleteButton && <div className={styles.embeddedActions}>{deleteButton}</div>
			) : (
				<div className={['entityEditActions', hasImage ? styles.actionsWithDelete : ''].filter(Boolean).join(' ')}>
					{deleteButton}
					<div className={styles.actionsRight}>
						<PunchupButton type="button" variant="secondary" onClick={handleCancel}>
							{t('button-cancel')}
						</PunchupButton>
						<PunchupButton type="submit" variant="primary" disabled={!hasStagedFile}>
							{t('button-save')}
						</PunchupButton>
					</div>
				</div>
			)}
		</div>
	);

	return embedded ? <PunchupFormModuleWrapper>{body}</PunchupFormModuleWrapper> : body;
};
